import { useEffect, useRef } from 'react';
import * as echarts from 'echarts';

/**
 * Custom hook to handle chart events and interactions
 */
export function useChartEventHandlers(
  chartInstance: React.MutableRefObject<echarts.ECharts | null>,
  selectedParam: string,
  isDark: boolean,
) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Handle resize
  useEffect(() => {
    const handleResize = () => {
      chartInstance.current?.resize();
    };

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [chartInstance]);

  // Handle fullscreen change
  useEffect(() => {
    const handleFullscreenChange = () => {
      if (chartInstance.current) {
        chartInstance.current.resize();
      }
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [chartInstance]);

  const resetZoom = () => {
    if (chartInstance.current) {
      chartInstance.current.dispatchAction({
        type: 'dataZoom',
        start: 0,
        end: 100,
      });
    }
  };

  const toggleFullscreen = () => {
    if (!containerRef.current) return;

    if (!document.fullscreenElement) {
      containerRef.current.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const downloadChart = () => {
    if (!chartInstance.current) return;

    // Store the current option
    const currentOption = chartInstance.current.getOption();

    // Add watermark to the option
    const optionWithWatermark = {
      ...currentOption,
      graphic: [
        {
          type: 'text',
          left: 10,
          bottom: 10,
          style: {
            text: 'Jua.ai',
            fontSize: 12,
            fontFamily: 'Inter, sans-serif',
            fill: isDark ? 'rgba(229, 231, 235, 0.5)' : 'rgba(55, 65, 81, 0.5)',
            fontWeight: 500,
          },
          z: 100,
        },
      ],
    };

    // Temporarily apply the watermark
    chartInstance.current.setOption(optionWithWatermark);

    // Get the base64 encoded image URL
    const url = chartInstance.current.getDataURL({
      type: 'png',
      pixelRatio: 2, // Higher quality for retina displays
      backgroundColor: isDark ? '#1F2937' : '#FFFFFF',
    });

    // Restore the original option
    chartInstance.current.setOption(currentOption);

    // Create a temporary link element
    const link = document.createElement('a');
    link.download = `forecast-${selectedParam}-${new Date().toISOString().split('T')[0]}.png`;
    link.href = url;

    // Trigger the download
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return {
    containerRef,
    resetZoom,
    toggleFullscreen,
    downloadChart,
  };
}
